import type { ArchiveRecord, BehaviorEvent, VisitSnapshot } from '../shared/types'

export interface VisitDraft {
  visitId: string
  visitorLabel: string
  startedAt: string
  activeDurationMs: number
  remainingMs: number
  events: BehaviorEvent[]
}

const DB_NAME = 'humanmuseum-threejs'
const DB_VERSION = 1
const DRAFTS = 'drafts'
const ARCHIVES = 'archives'
let openPromise: Promise<IDBDatabase> | undefined

function openDatabase(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined') return Promise.reject(new Error('当前环境不支持 IndexedDB。'))
  if (!openPromise) {
    openPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)
      request.onupgradeneeded = () => {
        const db = request.result
        if (!db.objectStoreNames.contains(DRAFTS)) db.createObjectStore(DRAFTS, { keyPath: 'visitId' })
        if (!db.objectStoreNames.contains(ARCHIVES)) db.createObjectStore(ARCHIVES, { keyPath: 'snapshot.visitId' })
      }
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error ?? new Error('无法打开本地档案库。'))
      request.onblocked = () => reject(new Error('本地档案库正在被其他页面升级，请关闭旧页面后重试。'))
    }).catch((error) => {
      openPromise = undefined
      throw error
    })
  }
  return openPromise!
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('本地存储操作失败。'))
  })
}

export async function listArchives(): Promise<ArchiveRecord[]> {
  const db = await openDatabase()
  const transaction = db.transaction(ARCHIVES, 'readonly')
  const rows = await requestResult(transaction.objectStore(ARCHIVES).getAll() as IDBRequest<ArchiveRecord[]>)
  return rows.sort((a, b) => b.snapshot.endedAt.localeCompare(a.snapshot.endedAt))
}

export async function getLatestDraft(): Promise<VisitDraft | undefined> {
  const db = await openDatabase()
  const rows = await requestResult(db.transaction(DRAFTS, 'readonly').objectStore(DRAFTS).getAll() as IDBRequest<VisitDraft[]>)
  return rows.sort((a, b) => b.startedAt.localeCompare(a.startedAt))[0]
}

export async function saveDraft(draft: VisitDraft): Promise<void> {
  const db = await openDatabase()
  const tx = db.transaction(DRAFTS, 'readwrite')
  tx.objectStore(DRAFTS).put(draft)
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error ?? new Error('参观草稿保存失败。'))
    tx.onabort = () => reject(tx.error ?? new Error('参观草稿保存已中断。'))
  })
}

export async function saveArchive(archive: ArchiveRecord): Promise<void> {
  const db = await openDatabase()
  const tx = db.transaction([DRAFTS, ARCHIVES], 'readwrite')
  tx.objectStore(ARCHIVES).put(archive)
  tx.objectStore(DRAFTS).delete(archive.snapshot.visitId)
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error ?? new Error('参观档案保存失败。'))
    tx.onabort = () => reject(tx.error ?? new Error('参观档案保存已中断。'))
  })
}

export async function updateArchive(archive: ArchiveRecord): Promise<void> {
  const db = await openDatabase()
  const tx = db.transaction(ARCHIVES, 'readwrite')
  tx.objectStore(ARCHIVES).put(archive)
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error ?? new Error('生成状态保存失败。'))
    tx.onabort = () => reject(tx.error ?? new Error('生成状态保存已中断。'))
  })
}

export function freezeSnapshot(snapshot: VisitSnapshot): VisitSnapshot {
  const events = snapshot.events.map((event) => Object.freeze({
    ...event,
    termIds: Object.freeze([...event.termIds]) as unknown as string[],
    visualTerms: Object.freeze([...event.visualTerms]) as unknown as string[],
  }))
  return Object.freeze({ ...snapshot, events: Object.freeze(events) as unknown as BehaviorEvent[] })
}
